import { assertNever } from "@ondo/shared";
import { useId } from "react";

import { LoadingScreen } from "../lib/Loading.tsx";
import { RoomCreateForm } from "./RoomCreateForm.tsx";
import { RoomDetail } from "./RoomDetail.tsx";
import { RoomLink, RoomList } from "./RoomList.tsx";
import { VIEWING_ROOMS_COPY } from "./viewingRooms.constants.ts";
import { useViewingRoomsController } from "./useViewingRoomsController.ts";
import "./viewingRooms.css";

/**
 * The collector's viewing rooms: the list, the form that makes one, and one
 * room's own screen.
 *
 * The only screen in ONDO that produces a public link. Everything the copy says
 * about what a room does and does not disclose is load-bearing — a collector who
 * believes a room shows less than it does will put the wrong work in one.
 */
export function ViewingRooms({
  handle,
  record,
  roomsHref,
}: {
  /** The account's public address. Null until claimed, which is the first gate. */
  handle: string | null;
  /** Which room the URL names, if any. */
  record: string | null;
  /** '/collector/me/viewing-rooms' */
  roomsHref: string;
}) {
  const controller = useViewingRoomsController({
    handle,
    record,
    roomsHref,
    // Read here rather than in the model, which imports no browser global — the
    // link has to be absolute to be worth copying, and the origin is the one
    // part of it this app does not otherwise know.
    origin: window.location.origin,
  });
  const { view } = controller;
  const handleFieldId = useId();

  switch (view.kind) {
    case "loading":
      return <LoadingScreen message={view.message} />;

    case "error":
      return (
        <p className="rooms__error" role="alert">
          {view.message}
        </p>
      );

    case "needs-handle":
      return (
        <section className="rooms">
          <p className="rooms__lead">{view.lead}</p>
          <p className="rooms__note">{view.detail}</p>

          <form
            className="rooms__field"
            onSubmit={(event) => {
              event.preventDefault();
              controller.claimHandle();
            }}
          >
            <label className="rooms__label" htmlFor={handleFieldId}>
              {view.label}
            </label>
            <input
              id={handleFieldId}
              className="rooms__input"
              value={view.value}
              placeholder={VIEWING_ROOMS_COPY.handlePlaceholder}
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => controller.typeHandle(event.target.value)}
            />
            {view.preview && <p className="rooms__hint">{view.preview}</p>}
            {view.problem && (
              <p className="rooms__error" role="alert">
                {view.problem}
              </p>
            )}

            <button type="submit" className="rooms__submit" disabled={view.busy}>
              {view.busy ? VIEWING_ROOMS_COPY.handleBusy : VIEWING_ROOMS_COPY.handleAction}
            </button>
          </form>
        </section>
      );

    case "empty":
      return (
        <section className="rooms">
          <p className="rooms__lead">{view.lead}</p>
          <p className="rooms__note">{view.detail}</p>
          <button type="button" className="rooms__submit" onClick={controller.startCreating}>
            {view.createLabel}
          </button>
        </section>
      );

    case "form":
      return (
        <section className="rooms">
          <RoomCreateForm
            heading={view.heading}
            submitLabel={view.submitLabel}
            busyLabel={view.busyLabel}
            liveNote={view.liveNote}
            editing={view.mode.kind === "edit"}
            draft={view.draft}
            source={view.source}
            problem={view.problem}
            error={view.error}
            busy={view.busy}
            picking={view.picking}
            preview={view.preview}
            onEdit={controller.editDraft}
            onKind={controller.chooseSource}
            onToggle={controller.toggleChosen}
            onOpenPicker={controller.startPicking}
            onClosePicker={controller.stopPicking}
            onSubmit={controller.submitDraft}
            onCancel={controller.cancelCreating}
          />
        </section>
      );

    case "created":
      return (
        <section className="rooms rooms__done">
          <p className="rooms__lead">{view.lead}</p>
          <h2 className="rooms__name">{view.row.title}</h2>
          <p className="rooms__meta">{view.row.meta}</p>
          <p className="rooms__shows">{view.row.shows}</p>

          <RoomLink
            url={view.row.url}
            copied={false}
            onCopy={() => controller.copyLink(view.row.id, view.row.url)}
          />

          <p className="rooms__note">{view.note}</p>

          <button type="button" className="rooms__action" onClick={controller.cancelCreating}>
            {VIEWING_ROOMS_COPY.createdDone}
          </button>
        </section>
      );

    case "detail":
      return (
        <RoomDetail
          row={view.row}
          visits={view.visits}
          backHref={view.backHref}
          busy={view.busy}
          error={view.error}
          copied={view.copied}
          confirming={view.confirming}
          preview={view.preview}
          onCopy={controller.copyLink}
          onHide={controller.hideRoom}
          onAskDelete={controller.askToDelete}
          onDelete={controller.deleteRoom}
          onEditRoom={controller.startEditing}
        />
      );

    case "list":
      return (
        <section className="rooms">
          <div className="rooms__actions">
            <button type="button" className="rooms__submit" onClick={controller.startCreating}>
              {view.createLabel}
            </button>
          </div>

          {view.error && (
            <p className="rooms__error" role="alert">
              {view.error}
            </p>
          )}

          <RoomList
            rows={view.rows}
            copiedId={view.copiedId}
            confirmingId={view.confirmingId}
            busy={view.busy}
            onCopy={controller.copyLink}
            onHide={controller.hideRoom}
            onAskDelete={controller.askToDelete}
            onDelete={controller.deleteRoom}
            onEditRoom={controller.startEditing}
          />
        </section>
      );

    default:
      return assertNever(view);
  }
}
